import React, { useEffect, useMemo, useState } from "react";
import { omit } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { components, OptionProps } from "react-select";
import { TAG_DISPLAY } from "common/constants";
import { Button } from "../../../../../../Common/Button";
import { Icon } from "../../../../../../Common/Icons/LucideIcons";
import { Body } from "../../../../../../Common/Typography";
import { FieldWrapper } from "../../../../../../DocumentUploads/Forms/GeneratedDocuments/FormFields/__styles__/FormFields";
import { Label } from "../../../../../../Inputs";
import { OptionContainer } from "../../../../../../Inputs/__styles__/MultiSelect";
import MultiSelect, { BaseOption } from "../../../../../../Inputs/MultiSelect";
import { Checkbox, Text } from "../../../../../../Inputs/react-hook-form";
import Select from "../../../../../../Inputs/Select";
import TextInput from "../../../../../../Inputs/Text";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  InputFieldPath,
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import {
  ActionIconContainer,
  HelpText,
  InputContainer,
  LabelContainer,
  LVLabel,
  OptionsContainer,
  ValueInputContainer,
  WidgetSettingsContainer,
} from "./__styles__/WidgetSettings";
import {
  Option,
  useCreateEditDropdownOptionModal,
} from "./CreateEditDropdownOptionModal";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
} from "./shared";

const DropdownWidgetSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
  inputType,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const { register, setValue, watch, control } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  let options = watch(`${fieldPath}.enum`);
  let optionsAsObjects = options?.map((option: string | Option) => {
    if (typeof option === "string") {
      return {
        label: option,
        value: option,
      };
    }
    return option;
  });

  const [dropdownOptions, setDropdownOptions] = useState<Array<Option>>(
    optionsAsObjects?.length ? optionsAsObjects : [{ label: "", value: "" }],
  );

  // Have to stringify, because the array can change lengths and useEffect doesn't like that
  const stringifiedDropdownOptions = JSON.stringify(dropdownOptions);

  useEffect(() => {
    setValue(`${fieldPath}.enum`, dropdownOptions, { shouldValidate: true });
  }, [stringifiedDropdownOptions]);

  const setDropdownOption = (index: number, updatedOption: Option) => {
    const updatedOptions = [...dropdownOptions];
    updatedOptions[index] = updatedOption;
    setDropdownOptions(updatedOptions);
  };

  const addDropdownOption = (newOption: Option) => {
    const updatedOptions = [...dropdownOptions, newOption];
    setDropdownOptions(updatedOptions);
  };

  const removeDropdownOption = (index: number) => {
    dropdownOptions.splice(index, 1);
    // creating a shallow copy of the array since splice modifies in place
    const updatedOptions = [...dropdownOptions];
    setDropdownOptions(updatedOptions);
  };

  const useAsTag = watch(`${fieldPath}.useAsTag`) ?? false;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    if (!useAsTag) {
      const updatedOptions = dropdownOptions.map(option =>
        omit(option, "display"),
      );
      setDropdownOptions(updatedOptions);
    } else {
      const updatedOptions = dropdownOptions.map(option => ({
        ...option,
        display: TAG_DISPLAY.NEUTRAL,
      }));
      setDropdownOptions(updatedOptions);
    }
  }, [useAsTag]);

  const [showCreateEditDropdownOptionModal] =
    useCreateEditDropdownOptionModal();

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={false}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Placeholder text"} />
        <Text {...register(`${fieldPath}.placeholder`)} disabled={readOnly} />
      </div>
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <div>
        <div>
          <Label
            style={{ marginBottom: "4px" }}
            required
            text={"Dropdown options"}
          />
          <HelpText style={{ marginTop: "0px", paddingBottom: "8px" }}>
            Labels are visible to users, while values are backend identifiers
            and hidden from them.
          </HelpText>
        </div>
      </div>
      <Checkbox
        control={control}
        name={`${fieldPath}.useAsTag`}
        label="Display selected label option on information panel"
        disabled={readOnly}
      />
      <div>
        <div>
          <LabelContainer>
            <LVLabel text={"Label"} />
            <LVLabel text={"Value"} />
          </LabelContainer>
          {dropdownOptions.map((option, index) => (
            <OptionsContainer key={`option.${index}`}>
              <InputContainer>
                <TextInput
                  name={`label.${index}`}
                  value={option.label}
                  disabled
                />
              </InputContainer>
              <ValueInputContainer>
                <TextInput
                  name={`value.${index}`}
                  value={option.value}
                  disabled
                />
              </ValueInputContainer>
              {!readOnly && (
                <ActionIconContainer>
                  <Icon
                    iconName="pencil"
                    color="contentSecondary"
                    size="18"
                    data-testid={`dropdown-option-edit-${index}`}
                    onClick={() => {
                      showCreateEditDropdownOptionModal({
                        useAsTag,
                        existingOption: option,
                        onDelete:
                          dropdownOptions.length > 1 && !isModuleInput ?
                            () => removeDropdownOption(index)
                          : undefined,
                        onSave: (props: Option) => {
                          setDropdownOption(index, props);
                        },
                        valueEditingDisabled: isModuleInput,
                      });
                    }}
                  />
                </ActionIconContainer>
              )}
            </OptionsContainer>
          ))}
        </div>
        <div style={{ paddingTop: "8px" }}>
          <Button
            styleVariant="outlineLight"
            size="small"
            leftIconName="plus"
            onClick={() =>
              showCreateEditDropdownOptionModal({
                useAsTag,
                onSave: addDropdownOption,
                // If you can create the option, you should be allowed to edit the value
                valueEditingDisabled: false,
              })
            }
            disabled={isModuleInput || readOnly}
          >
            Add option
          </Button>
        </div>
      </div>
      <DefaultValuesSection>
        <FieldWrapper data-testid="static-default-value-wrapper">
          {inputType === "multiSelect" ?
            <MultiSelectStaticDefaultInput
              fieldPath={fieldPath}
              readOnly={readOnly}
              options={dropdownOptions}
            />
          : <DropdownStaticDefaultInput
              fieldPath={fieldPath}
              readOnly={readOnly}
              dropdownOptions={dropdownOptions}
            />
          }
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

export const DropdownStaticDefaultInput = ({
  fieldPath,
  readOnly,
  dropdownOptions,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
  dropdownOptions: Array<Option>;
}) => {
  const { control, watch, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const dropdownOptionsValuesSet = useMemo(() => {
    return new Set(dropdownOptions.map(option => option.value));
  }, [dropdownOptions]);

  const rawDefaultValue = watch(`${fieldPath}.defaultValue`);
  const defaultValue = rawDefaultValue as Maybe<string>;

  useEffect(() => {
    if (defaultValue && !dropdownOptionsValuesSet.has(defaultValue)) {
      // ensure default value is reset if the selected dropdown option is removed
      setValue(`${fieldPath}.defaultValue`, undefined);
    }
  }, [defaultValue, dropdownOptionsValuesSet, fieldPath, setValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <Select
            value={defaultValue}
            label="Static default value"
            name={`${fieldPath}.defaultValue`}
            onChange={value => {
              // store no value (undefined) for ui:defaultValue instead of empty string in form state
              field.onChange(value ?? undefined);
            }}
            options={dropdownOptions}
            isClearable={true}
            disabled={readOnly}
          />
        );
      }}
    />
  );
};

export const MultiSelectStaticDefaultInput = ({
  fieldPath,
  readOnly,
  options,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
  options: Array<Option>;
}) => {
  const { control, watch, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValue = watch(`${fieldPath}.defaultValue`);
  const value = defaultValue as Maybe<Array<string>>;

  useEffect(() => {
    const optionsToDelete =
      value?.filter(option => !options.some(o => o.value === option)) ?? [];

    if (optionsToDelete.length > 0) {
      setValue(
        `${fieldPath}.defaultValue`,
        value?.filter(option => !optionsToDelete.includes(option)),
      );
    }
  }, [options, value, fieldPath, setValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <MultiSelect
            name={`${fieldPath}.defaultValue`}
            value={value ?? []}
            options={options}
            label="Static default value"
            disabled={readOnly}
            onChange={value => {
              field.onChange(value ?? []);
            }}
            renderOption={(
              option: BaseOption,
              props: OptionProps<BaseOption, true>,
            ) => (
              <components.Option {...props}>
                <OptionContainer>
                  <Body size="large" type="regular">
                    {option.label}
                  </Body>
                </OptionContainer>
              </components.Option>
            )}
          />
        );
      }}
    />
  );
};

DropdownWidgetSettings.displayName = "DropdownWidgetSettings";

export default DropdownWidgetSettings;
